import { baseApi } from './baseApi';
import type { ApiError, JsonObject, JsonValue } from './baseQuery';
import { driverArraySchema, driverSchema, validate } from '../schema';
import type { Driver, Identification } from '../../../types';
import {
    mapGetAllDriversResponse,
    mapGetSingleDriverResponse,
    mapModelDriverToApi,
    mapModelIdentificationToApi,
} from '../../apiEndpoints';
import { config } from '../../../config';

interface JsonLinks {
    next?: {
        href?: string;
    };
}

interface DriverTagMutationArgs {
    driverId: string;
    tagId: string;
}

interface DriverIdentificationArgs {
    driverId: string;
    identification: Identification;
}

interface DeleteIdentificationArgs {
    driverId: string;
    identificationId: string;
}

const asJsonObject = (value: JsonValue | null): JsonObject | null =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? value : null;

const getNextHref = (response: JsonValue | null): string | undefined => {
    const responseObject = asJsonObject(response);
    if (!responseObject) {
        return undefined;
    }

    const links = responseObject._links as JsonLinks | undefined;
    return links?.next?.href;
};

const validateDrivers = (drivers: Driver[]) => {
    validate(driverArraySchema, drivers);
    return drivers;
};

const validateDriver = (driver: Driver) => {
    validate(driverSchema, driver);
    return driver;
};

const omitEmptyOptionalDriverFields = (driver: Driver) =>
    Object.fromEntries(
        Object.entries(mapModelDriverToApi(driver)).filter(([, value]) => value !== '' && value !== null)
    );

const invalidateDriverCollections = (driver: Driver) => [
    { type: 'Driver' as const, id: driver.driverId },
    { type: 'DriverList' as const, id: 'LIST' },
    { type: 'UserList' as const, id: 'LIST' },
    { type: 'TagList' as const, id: driver.accountId },
];

const createDriverTagPayload = (tagId: string) => ({ id: tagId });

const maxTagDriverRetries = config.actions.apiEndpoints.maximumTagDriverRetries;
const sleepBetweenRetriesMs = config.actions.apiEndpoints.sleepBetweenTagDriverRetriesMs;

const sleep = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

export const driversApi = baseApi.injectEndpoints({
    endpoints: build => ({
        getDrivers: build.query<Driver[], void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const drivers: Driver[] = [];
                let nextLink: string | undefined;

                do {
                    const result = await baseQuery(
                        nextLink
                            ? {
                                  absoluteUrl: nextLink,
                              }
                            : {
                                  service: 'driverAdmin',
                                  path: '/drivers?embed=(identifications,tags)',
                              }
                    );

                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    drivers.push(...validateDrivers(mapGetAllDriversResponse({ body: result.data })));
                    nextLink = getNextHref(result.data);
                } while (nextLink);

                return { data: drivers };
            },
            providesTags: result => [
                { type: 'DriverList', id: 'LIST' },
                ...(result?.map(driver => ({ type: 'Driver' as const, id: driver.driverId })) ?? []),
            ],
        }),
        getDriver: build.query<Driver, string>({
            query: driverId => ({
                service: 'driverAdmin',
                path: `/drivers/${driverId}?embed=(identifications,tags)`,
            }),
            transformResponse: response => validateDriver(mapGetSingleDriverResponse({ body: response })),
            providesTags: (_result, _error, driverId) => [{ type: 'Driver', id: driverId }],
        }),
        saveDriver: build.mutation<Driver, Driver>({
            async queryFn(driver, _api, _extraOptions, baseQuery) {
                const result = await baseQuery({
                    service: 'driverAdmin',
                    path: `/drivers/${driver.driverId}`,
                    method: 'PUT',
                    body: omitEmptyOptionalDriverFields(driver),
                    headers: {
                        Accept: 'application/json',
                    },
                });

                if (result.error) {
                    return { error: result.error };
                }

                return { data: driver };
            },
            invalidatesTags: (_result, _error, driver) => invalidateDriverCollections(driver),
        }),
        deleteDriver: build.mutation<void, Driver>({
            query: driver => ({
                service: 'driverAdmin',
                path: `/drivers/${driver.driverId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, driver) => invalidateDriverCollections(driver),
        }),
        addIdentification: build.mutation<void, DriverIdentificationArgs>({
            query: ({ driverId, identification }) => ({
                service: 'driverAdmin',
                path: `/drivers/${driverId}/identifications/${identification.id}`,
                method: 'PUT',
                body: mapModelIdentificationToApi(identification),
                headers: {
                    Accept: 'application/json',
                },
            }),
            invalidatesTags: (_result, _error, args) => [
                { type: 'Driver', id: args.driverId },
                { type: 'DriverList', id: 'LIST' },
            ],
        }),
        deleteIdentification: build.mutation<void, DeleteIdentificationArgs>({
            query: ({ driverId, identificationId }) => ({
                service: 'driverAdmin',
                path: `/drivers/${driverId}/identifications/${identificationId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, args) => [
                { type: 'Driver', id: args.driverId },
                { type: 'DriverList', id: 'LIST' },
            ],
        }),
        tagDriver: build.mutation<void, DriverTagMutationArgs>({
            async queryFn(args, _api, _extraOptions, baseQuery) {
                let retry = 0;

                for (;;) {
                    const result = await baseQuery({
                        service: 'driverAdmin',
                        path: `/drivers/${args.driverId}/tags/${args.tagId}`,
                        method: 'PUT',
                        body: createDriverTagPayload(args.tagId),
                        headers: {
                            Accept: 'application/json',
                        },
                    });

                    if (!result.error) {
                        return { data: undefined };
                    }

                    if (retry >= maxTagDriverRetries) {
                        return { error: result.error as ApiError };
                    }

                    retry += 1;
                    await sleep(sleepBetweenRetriesMs);
                }
            },
            invalidatesTags: (_result, _error, args) => [
                { type: 'Driver', id: args.driverId },
                { type: 'DriverList', id: 'LIST' },
            ],
        }),
        untagDriver: build.mutation<void, DriverTagMutationArgs>({
            query: ({ driverId, tagId }) => ({
                service: 'driverAdmin',
                path: `/drivers/${driverId}/tags/${tagId}`,
                method: 'DELETE',
                headers: {
                    Accept: 'application/json',
                },
            }),
            invalidatesTags: (_result, _error, args) => [
                { type: 'Driver', id: args.driverId },
                { type: 'DriverList', id: 'LIST' },
            ],
        }),
    }),
});

export const {
    useDeleteDriverMutation,
    useGetDriverQuery,
    useGetDriversQuery,
    useLazyGetDriverQuery,
    useSaveDriverMutation,
    useAddIdentificationMutation,
    useDeleteIdentificationMutation,
    useTagDriverMutation,
    useUntagDriverMutation,
} = driversApi;
